import React, { useState, useContext, useEffect } from 'react';
import { Context } from '../../../store/Store.js';
import { getGeneratedDocuments } from '../../../services/fileUploadService.js';
import GeneratedDocumentsMenuItems from './GeneratedDocumentsMenuItems.js';
import socket from '../../System/Socket/socket.js';
import {
  ForgeIconButton,
  ForgePopover,
  ForgeIcon,
  ForgeBadge,
  ForgeTooltip,
} from '@tylertech/forge-react';
import { ToastComponent } from '@tylertech/forge';
import { messageManager } from '../../System/messageManager/messageManager.js';
import { removeExtension } from './documentsHelpers.js';
import './ReportsButton.scss';
import { REDUCER_ACTION_TYPES } from '../../../store/Reducer.js';
import { useUser } from '../../../contexts/userContext.js';
import { useNameConventions } from '../../../contexts/nameConventionsContext.js';

const ReportsButton = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const [isDocumentReady, setIsDocumentReady] = useState(false);
  const [state, dispatch] = useContext(Context);
  const { user } = useUser();
  const { nameConventions } = useNameConventions();

  const prepare = async () => {
    let data = await fetchData();
    dispatch({ type: REDUCER_ACTION_TYPES.SET_GENERATED_DOCUMENTS, payload: data });
  };

  useEffect(() => {
    if (user?.activeOrganization && !state.generatedDocuments) {
      prepare();
    }
  }, [user?.activeOrganization]);

  useEffect(() => {
    // refresh listeners so callbacks can work with the latest value in context
    socket.on('documentReady', updateDocuments);
    socket.on('documentReady', () =>
      dispatch({ type: REDUCER_ACTION_TYPES.GENERATING_DOCUMENT, payload: false })
    );
    socket.on('fileNameReady', addLoadingDocument);
    socket.on('error', handleErrorEvent);
    socket.on('error', () =>
      dispatch({ type: REDUCER_ACTION_TYPES.GENERATING_DOCUMENT, payload: false })
    );
    socket.on('previewError', handlePreviewError);
    socket.on('connect_error', handleConnectionError);

    return () => {
      socket.off('documentReady', updateDocuments);
      socket.off('documentReady', () =>
        dispatch({ type: REDUCER_ACTION_TYPES.GENERATING_DOCUMENT, payload: false })
      );
      socket.off('fileNameReady', addLoadingDocument);
      socket.off('error', handleErrorEvent);
      socket.off('error', () =>
        dispatch({ type: REDUCER_ACTION_TYPES.GENERATING_DOCUMENT, payload: false })
      );
      socket.off('previewError', handlePreviewError);
      socket.off('connect_error', handleConnectionError);
    };
  }, [state.generatedDocuments]);

  const updateDocuments = async (documentName) => {
    let documents = state.generatedDocuments;
    let updatedDocumentInd = documents.findIndex(
      (document) => document.documentName === documentName
    );
    if (updatedDocumentInd !== -1) {
      documents[updatedDocumentInd].ready = true;
      documents[updatedDocumentInd].generationDate = new Date();
    } else {
      const loadingDocumentName = removeExtension(documentName);
      documents.unshift({
        documentName,
        ready: true,
        generationDate: new Date(),
      });
      documents = documents.filter(
        (document) => document.documentName !== loadingDocumentName
      );
    }
    setIsDocumentReady(true);
    updateState(documents);
  };

  const addLoadingDocument = (documentName) => {
    let documents = state.generatedDocuments;

    documents.unshift({ documentName, ready: false });
    updateState(documents);
    setOpenMenu(true);
  };

  const updateState = (data) => {
    dispatch({ type: REDUCER_ACTION_TYPES.SET_GENERATED_DOCUMENTS, payload: data });
  };

  const handleErrorEvent = ({ documentName, message }) => {
    let content = '';
    if (documentName) {
      let generatingDocumentInd = state.generatedDocuments.findIndex(
        (document) =>
          document.documentName.includes(documentName) && !document.ready
      );
      state.generatedDocuments.splice(generatingDocumentInd, 1);
      updateState([...state.generatedDocuments]);
      content +=
        messageManager(nameConventions).DOCUMENT_GENERATOR.DOCUMENT_GENERATOR_DOCUMENT_ERROR(
          documentName
        );
    }
    ToastComponent.present({ message: content + message });
  };

  const handleConnectionError = () => {
    // clear buffered events while connection is down
    socket.sendBuffer = [];
    dispatch({ type: REDUCER_ACTION_TYPES.GENERATING_DOCUMENT, payload: false });
    ToastComponent.present({
      message:
        messageManager(nameConventions).DOCUMENT_GENERATOR.DOCUMENT_GENERATOR_CONNECTION_ERROR,
    });
  };

  const handlePreviewError = () => {
    ToastComponent.present({
      message:
        messageManager(nameConventions).DOCUMENT_GENERATOR.DOCUMENT_GENERATOR_PREVIEW_ERROR,
    });
  };

  const fetchData = async () => {
    try {
      let documents = (await getGeneratedDocuments()).data;
      documents.map((document) => (document.ready = true));
      return documents;
    } catch {
      return [];
    }
  };

  const handleMenuAndDocumentReadyState = (menu) => {
    setOpenMenu(menu);
    setIsDocumentReady(false);
  };

  return (
    <div slot="end">
      <ForgeIconButton
        aria-label="Show menu"
        className="show-menu-button"
        variant={'test'}
        id="document-generator-popover-trigger"
        theme="app-bar"
        onClick={() => handleMenuAndDocumentReadyState(!openMenu)}
      >
        <ForgeIcon external externalType="all" name="file_download" />
        <ForgeBadge slot="badge" dot hide={!isDocumentReady} />
      </ForgeIconButton>
      <ForgeTooltip
        slot="end"
        anchor="document-generator-popover-trigger"
        position="bottom"
      >
        {'Downloaded documents'}
      </ForgeTooltip>
      <ForgePopover
        className={'download-reports-popover'}
        anchor="document-generator-popover-trigger"
        open={openMenu}
        onClose={() => handleMenuAndDocumentReadyState(false)}
        on-forge-popover-toggle={(e) => {
          e.detail.newState === 'closed' &&
            handleMenuAndDocumentReadyState(false);
        }}
      >
        <div className="reports-popover-container">
          <h3 className="forge-typography--heading2 margin">Downloads</h3>
          <GeneratedDocumentsMenuItems />
        </div>
      </ForgePopover>
    </div>
  );
};

export default ReportsButton;
